import { useEffect, useState } from "react";
import Box from "elements/Box";
import { Button, TextField } from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import { useSnackbar } from "notistack";
import DateRange from "elements/DateRange";
import moment from "moment";
import ReportViewer from "./ReportViewer";
import { ApiRequest } from "GlobalFunctions";

export default function AccesosReports(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [loadingPopup, setLoadingPopup] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, []);

  const [formValues, setFormValues] = useState<any>({
    date: null,
    time_from: "00:00",
    time_to: "23:59",
  });

  const [data, setData] = useState<any>([]);
  const [openViewer, setOpenViewer] = useState(false);

  const getReport = async (download = "") => {
    if (!formValues.date?.startDate || !formValues.date?.endDate) {
      enqueueSnackbar("Debes seleccionar un rango de fechas", { variant: "warning" });
      return;
    }

    setLoading(true);
    setLoadingPopup(true);

    const date_from = `${formValues.date.startDate} ${formValues.time_from}:00`;
    const date_to   = `${formValues.date.endDate} ${formValues.time_to}:59`;

    const isExport = download === "pdf" || download === "excel";

    await ApiRequest({
      url: isExport ? "/facility-access/export" : "/facility-access/report",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        date_from,
        date_to,
        ...(isExport ? { download } : {}),
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          if (isExport) {
            if (download === "pdf")   window.open(response.pdfUrl,  "_blank");
            if (download === "excel") window.open(response.fileUrl, "_blank");
          } else {
            // Transform records into tableHeaders + data for ReportViewer
            const tableHeaders = [
              "Documento",
              "Nombre",
              "Teléfono",
              "Email",
              "EPS",
              "ARL",
              "Se dirige a",
              "Contacto emergencia",
              "Observaciones",
              "Dispositivos",
              "Imágenes entrada",
              "Ingreso",
              "Imágenes salida",
              "Comentarios salida",
              "Salida",
              "Estado",
            ];

            const rows = (response.data || []).map((rec: any) => {
              const devices = safeJson(rec.devices);
              const images  = safeJson(rec.images);
              const exitImages = safeJson(rec.exit_images);

              const devicesStr = devices.length
                ? devices
                    .map((d: any) =>
                      d.serial_number
                        ? `${d.description} (S/N: ${d.serial_number})`
                        : d.description
                    )
                    .join(" | ")
                : "—";

              const status = rec.exit_at ? "SALIÓ" : "DENTRO";

              return {
                Documento: rec.document_number,
                Nombre: `${rec.first_name} ${rec.last_name}`,
                Teléfono: rec.phone || "—",
                Email: rec.email || "—",
                EPS: rec.eps || "—",
                ARL: rec.arl || "—",
                "Se dirige a": rec.destination || "—",
                "Contacto emergencia": rec.emergency_contact || "—",
                Observaciones: rec.observations || "—",
                Dispositivos: devicesStr,
                "Imágenes entrada": images.length ? images.map((_: any, i: number) => `Foto ${i + 1}`).join(" | ") : "—",
                Ingreso: rec.entry_at ? moment(rec.entry_at).format("DD/MM/YY HH:mm") : "—",
                "Imágenes salida": exitImages.length ? exitImages.map((_: any, i: number) => `Foto ${i + 1}`).join(" | ") : "—",
                "Comentarios salida": rec.exit_comments || "—",
                Salida: rec.exit_at ? moment(rec.exit_at).format("DD/MM/YY HH:mm") : "—",
                Estado: status,
                _images: images,
                _exit_images: exitImages,
              };
            });

            setData({ tableHeaders, data: rows });
            setOpenViewer(true);
          }
        } else {
          enqueueSnackbar(response.message || "Error al obtener el reporte", { variant: "error" });
        }
        setLoading(false);
        setLoadingPopup(false);
      },
    });
  };

  return (
    <>
      <div id="AccesosList" className="ModuleContainer">
        <div className="ModuleWrapper">
          <div className="boxContainerHeader">
            <div className="boxContainerTitle">
              <h2 className="h2_title">Reportes Accesos</h2>
              <span className="h2_subtitle">Selecciona el rango de fechas y horas</span>
            </div>
          </div>

          <Box id="AccesosBox" className="box100">

            <DateRange
              className="filters_datePicker"
              label="Rango de fechas"
              variant="outlined"
              fullWidth
              value={{
                startDate: formValues.date?.startDate || null,
                endDate:   formValues.date?.endDate   || null,
              }}
              format="MMMM DD, yyyy"
              onChange={(e: any) => {
                if (e.startDate && e.endDate) {
                  setFormValues({
                    ...formValues,
                    date: {
                      startDate: moment(e.startDate).format("YYYY-MM-DD"),
                      endDate:   moment(e.endDate).format("YYYY-MM-DD"),
                    },
                  });
                } else {
                  setFormValues({ ...formValues, date: null });
                }
              }}
              variantDatePicker="side-by-side"
            />

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", width: "100%" }}>
              <TextField
                label="Hora inicio"
                type="time"
                variant="outlined"
                style={{ flex: "1 1 160px" }}
                value={formValues.time_from}
                onChange={(e) =>
                  setFormValues({ ...formValues, time_from: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Hora fin"
                type="time"
                variant="outlined"
                style={{ flex: "1 1 160px" }}
                value={formValues.time_to}
                onChange={(e) =>
                  setFormValues({ ...formValues, time_to: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </div>

            <Button
              variant="contained"
              color="primary"
              onClick={() => getReport()}
              startIcon={<CloudDownloadOutlinedIcon />}
            >
              Ver
            </Button>

          </Box>
        </div>
      </div>

      <ReportViewer
        open={openViewer}
        setOpen={setOpenViewer}
        data={data}
        loading={loadingPopup}
        setLoading={setLoading}
        reportName="accesos"
        getReport={getReport}
        exportParams={formValues}
      />
    </>
  );
}

// Safe JSON parse helper
function safeJson(val: any): any[] {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try { return JSON.parse(val); } catch { return []; }
  }
  return [];
}
